import {
  buildSeries,
  CATEGORY_LABEL,
  earliestDay,
  entryChange,
  entryHistory,
  HISTORY_RANGES,
  type HistoryRange,
  programCategory,
  rangeStart,
  redemptionEntry,
} from '@trips/shared/balances';
import { formatFullDate, formatMonthDay } from '@trips/shared/format';
import type { BalanceSnapshot, PointsCategory, Program } from '@trips/shared/schema';
import { POINTS_CATEGORIES } from '@trips/shared/schema-consts';
import { pointValue } from '@trips/shared/wallet';
import { useMemo, useRef, useState } from 'react';
import { patch, put, remove } from '../../data/mutate';
import { selectPrefs, selectToday, selectWallet } from '../../data/selectors';
import { useStore } from '../../data/store';
import { Link } from '../../lib/router';
import { Button } from '../../ui/Button';
import { EmptyState } from '../../ui/EmptyState';
import { FieldRow, TextField } from '../../ui/Field';
import { Icon } from '../../ui/Icon';
import { Segmented } from '../../ui/Segmented';
import { toast } from '../../ui/Toast';
import { TopBar } from '../../ui/TopBar';
import { BalanceChart, type ChartDot, readoutPoints } from './BalanceChart';
import s from './Balances.module.css';
import { pts, usd } from './WalletScreen';

/**
 * Balances over time: the total of a wallet tab (or one program) as a step line over a chosen
 * range, in points or in dollars at today's valuations, with each program's change below.
 */

type Tab = 'all' | PointsCategory;
type Unit = 'points' | 'usd';
type SortKey = 'balance' | 'change' | 'value' | 'updated' | 'name';

const RANGE_LABEL: Record<HistoryRange, string> = {
  '30d': '30D',
  '3m': '3M',
  '6m': '6M',
  '1y': '1Y',
  all: 'All',
};
const RANGE_WORDS: Record<HistoryRange, string> = {
  '30d': 'the last 30 days',
  '3m': 'the last 3 months',
  '6m': 'the last 6 months',
  '1y': 'the last year',
  all: 'all time',
};
const SORTS: { key: SortKey; label: string }[] = [
  { key: 'balance', label: 'Balance' },
  { key: 'change', label: 'Change' },
  { key: 'value', label: 'Value' },
  { key: 'updated', label: 'Updated' },
  { key: 'name', label: 'Name' },
];

const signed = (n: number, f: (v: number) => string) =>
  n > 0 ? `+${f(n)}` : n < 0 ? `−${f(-n)}` : f(0);
const percent = (p: number | null) =>
  p == null ? '' : `${p > 0 ? '+' : p < 0 ? '−' : ''}${Math.abs(p * 100).toFixed(1)}%`;

/** 11.2M, 950k, 1,250. */
function compactNumber(n: number): string {
  const a = Math.abs(n);
  if (a >= 1e6) return `${(n / 1e6).toFixed(a >= 1e7 ? 1 : 2).replace(/\.?0+$/, '')}M`;
  if (a >= 1e4) return `${Math.round(n / 1e3)}k`;
  if (a >= 1e3) return `${(n / 1e3).toFixed(1).replace(/\.0$/, '')}k`;
  return String(Math.round(n));
}

function Change({ n, pct, f }: { n: number; pct?: number | null; f: (v: number) => string }) {
  const tone = n > 0 ? s.up : n < 0 ? s.down : s.flat;
  return (
    <span className={`${s.change} ${tone} num`}>
      {n !== 0 && <Icon name={n > 0 ? 'caret-up' : 'caret-down'} size={12} />}
      {n === 0 ? 'No change' : signed(n, f)}
      {pct != null && n !== 0 && <span className={s.pct}> {percent(pct)}</span>}
    </span>
  );
}

export function BalancesScreen({ entry: initialEntry }: { entry: string | null }) {
  const wallet = useStore(selectWallet);
  const snapTable = useStore((st) => st.tables.balanceSnapshots);
  const today = useStore(selectToday);
  const prefs = useStore(selectPrefs);
  const start = wallet.programs.find((p) => p.id === initialEntry);
  const [tab, setTab] = useState<Tab>(start ? programCategory(start) : 'all');
  const [range, setRange] = useState<HistoryRange>('3m');
  const [unit, setUnit] = useState<Unit>('points');
  const [entryId, setEntryId] = useState<string | null>(start?.id ?? null);
  const [sort, setSort] = useState<{ key: SortKey; desc: boolean }>({ key: 'balance', desc: true });
  /** The day being read on the chart: a hover preview, or kept by a tap or click. */
  const [pick, setPick] = useState<{ on: string; pinned: boolean } | null>(null);
  const chartRef = useRef<HTMLDivElement>(null);

  const snaps = useMemo(() => [...snapTable.values()], [snapTable]);
  const histories = useMemo(
    () => new Map(wallet.programs.map((p) => [p.id, entryHistory(p, snaps, today)])),
    [wallet.programs, snaps, today],
  );
  const cpp = useMemo(
    () =>
      new Map(
        wallet.programs.map((p) => [p.id, pointValue(p.programId, wallet.redemptions)?.main]),
      ),
    [wallet.programs, wallet.redemptions],
  );
  const from = rangeStart(range, today, earliestDay(histories.values()));
  const inTab = useMemo(
    () => wallet.programs.filter((p) => tab === 'all' || programCategory(p) === tab),
    [wallet.programs, tab],
  );
  const selected = entryId ? wallet.programs.find((p) => p.id === entryId) : undefined;
  const charted = useMemo(() => (selected ? [selected] : inTab), [selected, inTab]);
  const series = useMemo(
    () =>
      buildSeries({
        entries: charted,
        histories,
        from,
        to: today,
        weight:
          unit === 'usd'
            ? (e) => {
                const c = cpp.get(e.id);
                return c == null ? null : c / 100;
              }
            : undefined,
      }),
    [charted, histories, from, today, unit, cpp],
  );
  const dots = useMemo<ChartDot[]>(
    () =>
      selected
        ? wallet.redemptions
            .filter(
              (r) =>
                r.on >= from &&
                r.on <= today &&
                redemptionEntry(r, wallet.programs)?.id === selected.id,
            )
            .map((r) => ({
              on: r.on,
              label: `${r.points ? `${pts(r.points)} · ` : ''}${r.title}`,
            }))
        : [],
    [selected, wallet.redemptions, wallet.programs, from, today],
  );
  const points = useMemo(() => readoutPoints(series, dots), [series, dots]);
  const active = pick ? points.find((p) => p.on === pick.on) : undefined;

  const rows = useMemo(() => {
    const list = inTab.map((p) => {
      const c = entryChange(p, histories.get(p.id) ?? [], from, today);
      const v = cpp.get(p.id);
      return { p, c, value: v == null ? null : (p.balance * v) / 100 };
    });
    const dir = sort.desc ? -1 : 1;
    const key = (r: (typeof list)[number]): number | string =>
      sort.key === 'balance'
        ? r.p.balance
        : sort.key === 'change'
          ? r.c.change
          : sort.key === 'value'
            ? (r.value ?? -1)
            : sort.key === 'updated'
              ? (r.c.updatedOn ?? '')
              : r.p.name.toLowerCase();
    return list.sort((a, b) => {
      const ka = key(a);
      const kb = key(b);
      return (ka < kb ? -1 : ka > kb ? 1 : 0) * dir || a.p.name.localeCompare(b.p.name);
    });
  }, [inTab, histories, from, today, cpp, sort]);

  if (!wallet.programs.length)
    return (
      <div>
        <TopBar title="Over time" backTo="/wallet" />
        <EmptyState icon="chart-line-up" title="No points yet">
          <p>Add a points balance to start its history.</p>
        </EmptyState>
      </div>
    );

  const fmt = unit === 'usd' ? usd : pts;
  const now = series.at(-1)?.value ?? 0;
  const then = series[0]?.value ?? 0;
  const change = now - then;
  const left = unit === 'usd' ? charted.filter((p) => cpp.get(p.id) == null) : [];
  const choose = (id: string | null) => {
    setEntryId(id);
    setPick(null);
    chartRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };
  const context = selected
    ? selected.name
    : tab === 'all'
      ? 'All points and miles'
      : CATEGORY_LABEL[tab];
  // Changes show on the day a balance was logged, though they built up since the one before.
  const builtUp = active?.moves.some((m) => m.since && active.prevOn && m.since < active.prevOn);

  return (
    <div>
      <TopBar title="Over time" backTo="/wallet" />
      <div className={s.screen}>
        <h1 className="t-title1">Over time</h1>

        {selected ? (
          <button type="button" className={s.backChip} onClick={() => choose(null)}>
            <Icon name="caret-left" size={14} />
            {tab === 'all' ? 'All points' : CATEGORY_LABEL[tab]}
          </button>
        ) : (
          <fieldset className={s.chips}>
            <legend className="sr-only">Which points</legend>
            {(['all', ...POINTS_CATEGORIES] as Tab[]).map((t) => (
              <button
                key={t}
                type="button"
                className={s.chip}
                aria-pressed={tab === t}
                onClick={() => {
                  setTab(t);
                  setPick(null);
                }}
              >
                {t === 'all' ? 'All' : CATEGORY_LABEL[t]}
              </button>
            ))}
          </fieldset>
        )}

        {charted.length === 0 ? (
          <p className={s.empty}>
            No {tab === 'all' ? '' : `${CATEGORY_LABEL[tab].toLowerCase()} `}points in the wallet
            yet.
          </p>
        ) : (
          <>
            <div className={s.head} ref={chartRef}>
              <div className={s.headText}>
                <span className={s.headLabel}>
                  {context}
                  {active && <span className="num"> · {formatFullDate(active.on, prefs)}</span>}
                  {pick?.pinned && (
                    <button
                      type="button"
                      className={s.clear}
                      aria-label="Back to today"
                      title="Back to today"
                      onClick={() => setPick(null)}
                    >
                      <Icon name="x" size={12} />
                    </button>
                  )}
                </span>
                <span className={`${s.total} num`}>{fmt(active ? active.value : now)}</span>
                {active ? (
                  <span className={s.headChange}>
                    {active.change == null || !active.prevOn ? (
                      <span className={s.since}>Start of the range</span>
                    ) : (
                      <>
                        <Change
                          n={active.change}
                          pct={
                            active.value - active.change
                              ? active.change / (active.value - active.change)
                              : null
                          }
                          f={fmt}
                        />
                        <span className={s.since}>
                          since {formatMonthDay(active.prevOn, prefs)}
                        </span>
                      </>
                    )}
                  </span>
                ) : (
                  <span className={s.headChange}>
                    <Change n={change} pct={then ? change / then : null} f={fmt} />
                    <span className={s.since}>
                      {range === 'all' || from > rangeStart(range, today, null)
                        ? `since ${formatMonthDay(from, prefs)}`
                        : `over ${RANGE_WORDS[range]}`}
                    </span>
                  </span>
                )}
                {unit === 'usd' && (
                  <span className={s.footnote}>
                    At today’s valuations
                    {left.length > 0 && `; leaves out ${left.map((p) => p.name).join(' and ')}`}
                  </span>
                )}
              </div>
              <div className={s.unit}>
                <Segmented
                  label="Show"
                  value={unit}
                  onChange={setUnit}
                  options={[
                    { value: 'points', label: 'Pts' },
                    { value: 'usd', label: '$' },
                  ]}
                />
              </div>
            </div>

            <Segmented
              label="Range"
              value={range}
              onChange={(r) => {
                setRange(r);
                setPick(null);
              }}
              options={HISTORY_RANGES.map((r) => ({ value: r, label: RANGE_LABEL[r] }))}
            />

            <BalanceChart
              series={series}
              points={points}
              dots={dots}
              format={fmt}
              compact={(n) => (unit === 'usd' ? `${compactNumber(n)}` : compactNumber(n))}
              prefs={prefs}
              label={selected ? selected.name : 'Points total'}
              picked={pick?.on ?? null}
              pinned={pick?.pinned ?? false}
              onPick={(on, pinned) => setPick(on ? { on, pinned } : null)}
            />
            {dots.length > 0 && (
              <p className={s.legend}>
                <span className={s.legendDot} aria-hidden="true" /> Redemptions
              </p>
            )}

            {active && pick?.pinned ? (
              <section className={s.section} aria-live="polite">
                <h2 className={s.sectionHead}>
                  {active.change == null
                    ? `Balances on ${formatMonthDay(active.on, prefs)}`
                    : `What changed on ${formatMonthDay(active.on, prefs)}`}
                </h2>
                {active.moves.length + active.dots.length > 0 ? (
                  <div className={s.rows}>
                    {active.moves.map((m) => (
                      <div key={m.id} className={s.row}>
                        <span className={s.rowText}>
                          <span className={s.rowTitle}>{m.name}</span>
                          <span className={s.sub}>
                            {[
                              m.since
                                ? `since ${formatMonthDay(m.since, prefs)}`
                                : 'first balance logged',
                              ...m.notes,
                            ].join(' · ')}
                          </span>
                        </span>
                        <span className={s.rowRight}>
                          <Change n={m.change} f={fmt} />
                        </span>
                      </div>
                    ))}
                    {active.dots.map((d) => (
                      <div key={d} className={s.row}>
                        <span className={s.rowText}>
                          <span className={s.rowTitle}>
                            <span className={s.legendDot} aria-hidden="true" /> Redeemed
                          </span>
                          <span className={s.sub}>{d}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className={s.hint}>
                    {active.change == null
                      ? 'The start of the chart.'
                      : 'Nothing was logged that day.'}
                  </p>
                )}
                {builtUp && (
                  <p className={s.hint}>
                    A change shows on the day its balance was logged. It built up any time since the
                    date under each program.
                  </p>
                )}
              </section>
            ) : (
              <p className={s.hint}>Tap or click the chart to see what changed on a day.</p>
            )}

            {selected && (
              <Snapshots
                program={selected}
                snaps={snaps.filter((x) => x.entryId === selected.id && !x.deleted)}
                today={today}
              />
            )}

            <section className={s.section}>
              <div className={s.sortRow}>
                <h2 className={s.sectionHead}>Programs</h2>
                <fieldset className={s.sorts}>
                  <legend className="sr-only">Sort by</legend>
                  {SORTS.map((o) => (
                    <button
                      key={o.key}
                      type="button"
                      className={s.sort}
                      aria-pressed={sort.key === o.key}
                      onClick={() =>
                        setSort((cur) =>
                          cur.key === o.key
                            ? { key: o.key, desc: !cur.desc }
                            : { key: o.key, desc: o.key !== 'name' },
                        )
                      }
                    >
                      {o.label}
                      {sort.key === o.key && (
                        <Icon name={sort.desc ? 'caret-down' : 'caret-up'} size={11} />
                      )}
                    </button>
                  ))}
                </fieldset>
              </div>
              <div className={s.table}>
                <div className={`${s.tr} ${s.th}`} aria-hidden="true">
                  <span className={s.cName}>Program</span>
                  <span className={s.cBal}>Balance</span>
                  <span className={s.cChg}>Change</span>
                  <span className={s.cVal}>Value</span>
                </div>
                {rows.map(({ p, c, value }) => (
                  <button
                    key={p.id}
                    type="button"
                    className={s.tr}
                    aria-pressed={p.id === entryId}
                    onClick={() => choose(p.id === entryId ? null : p.id)}
                  >
                    <span className={s.cName}>
                      <span className={s.nameText}>{p.name}</span>
                      <span className={s.sub}>
                        {c.updatedOn
                          ? `Updated ${formatMonthDay(c.updatedOn, prefs)}`
                          : 'Not checked'}
                      </span>
                    </span>
                    <span className={`${s.cBal} num`}>{pts(p.balance)}</span>
                    <span className={s.cChg}>
                      <Change n={c.change} pct={c.pct} f={pts} />
                    </span>
                    <span className={`${s.cVal} num`}>
                      {value == null ? <span className={s.sub}>—</span> : usd(value)}
                    </span>
                  </button>
                ))}
                {rows.length > 1 && (
                  <div className={`${s.tr} ${s.sum}`}>
                    <span className={s.cName}>Total</span>
                    <span className={`${s.cBal} num`}>
                      {pts(rows.reduce((n, r) => n + r.p.balance, 0))}
                    </span>
                    <span className={s.cChg}>
                      <Change n={rows.reduce((n, r) => n + r.c.change, 0)} f={pts} />
                    </span>
                    <span className={`${s.cVal} num`}>
                      {usd(rows.reduce((n, r) => n + (r.value ?? 0), 0))}
                    </span>
                  </div>
                )}
              </div>
              {selected && (
                <Link to={`/wallet/points/${selected.id}`} className={s.more}>
                  {selected.name} details
                  <Icon name="caret-right" size={14} />
                </Link>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}

/** One program's logged balances, newest first, each one editable. */
function Snapshots({
  program,
  snaps,
  today,
}: {
  program: Program;
  snaps: BalanceSnapshot[];
  today: string;
}) {
  const prefs = useStore(selectPrefs);
  const [editing, setEditing] = useState<string | null>(null);
  const history = entryHistory(program, snaps, today);
  const listed = history
    .map((h, i) => ({ h, prev: i > 0 ? history[i - 1]!.balance : null }))
    .filter((x) => x.h.snapshotId)
    .reverse();
  if (!listed.length) return null;
  return (
    <section className={s.section}>
      <h2 className={s.sectionHead}>Logged balances</h2>
      <div className={s.rows}>
        {listed.map(({ h, prev }) => {
          const snap = snaps.find((x) => x.id === h.snapshotId)!;
          if (editing === snap.id)
            return (
              <SnapshotEditor
                key={snap.id}
                snap={snap}
                today={today}
                done={() => setEditing(null)}
              />
            );
          return (
            <button
              key={snap.id}
              type="button"
              className={s.row}
              onClick={() => setEditing(snap.id)}
            >
              <span className={s.rowText}>
                <span className={s.rowTitle}>{formatFullDate(snap.on, prefs)}</span>
                {snap.note && <span className={s.sub}>{snap.note}</span>}
              </span>
              <span className={s.rowRight}>
                <span className={`${s.rowValue} num`}>{pts(snap.balance)}</span>
                {prev != null && <Change n={snap.balance - prev} f={pts} />}
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

function SnapshotEditor({
  snap,
  today,
  done,
}: {
  snap: BalanceSnapshot;
  today: string;
  done: () => void;
}) {
  const [on, setOn] = useState(snap.on);
  const [balance, setBalance] = useState(String(snap.balance));
  const [note, setNote] = useState(snap.note ?? '');
  const [error, setError] = useState<string | null>(null);
  const save = () => {
    const b = Number(balance.replace(/[,\s]/g, ''));
    if (!/^\d{4}-\d{2}-\d{2}$/.test(on) || on > today) return setError('Choose a day up to today.');
    if (!Number.isFinite(b) || b < 0) return setError('Enter the balance.');
    void patch('balanceSnapshots', snap.id, {
      on,
      balance: Math.round(b),
      note: note.trim().slice(0, 300) || null,
    });
    done();
  };
  const del = () => {
    void remove('balanceSnapshots', snap.id);
    toast('Balance removed from history', {
      action: { label: 'Undo', run: () => void put('balanceSnapshots', snap) },
    });
    done();
  };
  return (
    <form
      className={s.editor}
      onSubmit={(e) => {
        e.preventDefault();
        save();
      }}
    >
      <FieldRow>
        <TextField label="Day" type="date" value={on} max={today} onChange={setOn} />
        <TextField label="Balance" value={balance} onChange={setBalance} inputMode="numeric" />
      </FieldRow>
      <TextField label="Note" value={note} onChange={setNote} placeholder="After a redemption" />
      {error && <p className={s.error}>{error}</p>}
      <div className={s.editorButtons}>
        <Button variant="ghost" size="sm" icon="trash" onClick={del}>
          Delete
        </Button>
        <span className={s.grow} />
        <Button variant="quiet" size="sm" onClick={done}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" size="sm">
          Save
        </Button>
      </div>
    </form>
  );
}
